/**
 * Transient combat effects, all at internal resolution (doc 10): harpoon
 * tracers, arc lightning (jittered pixel segments), mortar shells on a lob,
 * expanding blast rings, death puffs. Everything is pooled sprites over
 * generated textures; per-shot textures die with their effect.
 */

import { Container, Sprite, Texture } from 'pixi.js';
import { TH, TW } from '../render/iso.ts';
import { OUTLINE_HOVER, SEMANTIC, type DepthPalette } from '../render/palette.ts';
import { isoDiamondRim, linePx } from '../render/primitives.ts';
import { hex, PixelSurface, shade } from '../render/surface.ts';
import { surfaceToTexture, TextureCache } from '../render/pipeline.ts';

interface Effect {
  sprite: Sprite;
  ttl: number;
  own: Texture | null;
  /** Shells animate along a lob; everything else just expires. */
  lob?: { x0: number; y0: number; x1: number; y1: number; total: number };
}

/** World px from tile-space floats. */
function worldX(x: number, y: number): number {
  return Math.round((x - y) * TW);
}
function worldY(x: number, y: number): number {
  return Math.round((x + y) * TH + TH);
}

export class EffectsView {
  private readonly effects: Effect[] = [];

    private readonly layer: Container;
  private readonly cache: TextureCache;
  private readonly depth: DepthPalette;

  /** Doc 18 §3's Flash Intensity, read fresh so the setting applies at once. */
  private readonly flash: () => number;

  constructor(layer: Container, cache: TextureCache, depth: DepthPalette, flash: () => number = () => 1) {
    this.layer = layer;
    this.cache = cache;
    this.depth = depth;
    this.flash = flash;
  }

  get count(): number {
    return this.effects.length;
  }

  private add(sprite: Sprite, ttl: number, own: Texture | null, lob?: Effect['lob']): void {
    sprite.zIndex = 9999;
    this.layer.addChild(sprite);
    const e: Effect = { sprite, ttl, own };
    if (lob) e.lob = lob;
    this.effects.push(e);
  }

  tracer(x0: number, y0: number, x1: number, y1: number): void {
    const minX = Math.min(x0, x1) - 2;
    const minY = Math.min(y0, y1) - 2;
    const s = new PixelSurface(Math.abs(x1 - x0) + 5, Math.abs(y1 - y0) + 5);
    linePx(s, x0 - minX, y0 - minY, x1 - minX, y1 - minY, hex('#e8f4f2'));
    const tex = surfaceToTexture(s);
    const sprite = new Sprite(tex);
    sprite.x = minX;
    sprite.y = minY;
    this.add(sprite, 3, tex);
  }

  /**
   * Doc 10: chain lightning = jittered pixel segments between the victims.
   * Doc 17 §7 names this as one of the two things Flash Intensity clamps: at
   * Reduced the elbows stop moving, at Off the arc is a straight dim line.
   */
  chain(points: Array<{ x: number; y: number }>): void {
    if (points.length < 2) return;
    const flash = this.flash();
    const px = points.map((p) => ({ x: worldX(p.x, p.y), y: worldY(p.x, p.y) - 8 }));
    const minX = Math.min(...px.map((p) => p.x)) - 3;
    const minY = Math.min(...px.map((p) => p.y)) - 3;
    const s = new PixelSurface(
      Math.max(...px.map((p) => p.x)) - minX + 6,
      Math.max(...px.map((p) => p.y)) - minY + 6,
    );
    const arc = this.depth.playerGlow;
    for (let k = 0; k < px.length - 1; k++) {
      const a = px[k]!;
      const b = px[k + 1]!;
      // one elbow per segment, jittered — render-side randomness is fine
      const wobble = flash >= 1 ? 5 : 0;
      const mx = ((a.x + b.x) >> 1) + (wobble ? Math.floor(Math.random() * wobble) - 2 : 0);
      const my = ((a.y + b.y) >> 1) + (wobble ? Math.floor(Math.random() * wobble) - 2 : 0);
      linePx(s, a.x - minX, a.y - minY, mx - minX, my - minY, arc);
      linePx(s, mx - minX, my - minY, b.x - minX, b.y - minY, shade(arc, flash > 0 ? 1.4 : 0.7));
    }
    const tex = surfaceToTexture(s);
    const sprite = new Sprite(tex);
    sprite.x = minX;
    sprite.y = minY;
    this.add(sprite, 4, tex);
  }

  /** A mortar shell on its lob; the parabola is pure presentation. */
  shell(fromX: number, fromY: number, toX: number, toY: number, flightTicks: number): void {
    const sprite = new Sprite(
      this.cache.get('fx:shell', () => {
        const s = new PixelSurface(3, 3);
        s.fillRect(0, 0, 3, 3, shade(this.depth.structureBase, 1.6), 'set');
        s.px(0, 0, hex('#e8f4f2'));
        return s.resetOps();
      }),
    );
    this.add(sprite, flightTicks, null, {
      x0: worldX(fromX, fromY),
      y0: worldY(fromX, fromY) - 14,
      x1: worldX(toX, toY),
      y1: worldY(toX, toY) - 2,
      total: flightTicks,
    });
  }

  explosion(x: number, y: number, radius: number): void {
    const halfH = Math.max(2, Math.round(radius * TH));
    const key = `fx:boom:${halfH}`;
    const sprite = new Sprite(
      this.cache.get(key, () => {
        const s = new PixelSurface(halfH * 4 + 4, halfH * 2 + 4);
        isoDiamondRim(s, s.width >> 1, s.height >> 1, halfH, SEMANTIC.warning, true, 0);
        isoDiamondRim(s, s.width >> 1, s.height >> 1, Math.max(1, halfH - 2), SEMANTIC.critical, true, 1);
        return s.resetOps();
      }),
    );
    sprite.x = worldX(x, y) - (sprite.texture.width >> 1);
    sprite.y = worldY(x, y) - (sprite.texture.height >> 1);
    this.add(sprite, 6, null);
  }

  /**
   * A big kill: a bright expanding ring on top of the puff (doc 17 §7). `flash`
   * is the doc 18 §3 intensity clamp — at Off this draws nothing at all, which
   * is the point of the setting.
   */
  flare(x: number, y: number, flash: number): void {
    if (flash <= 0) return;
    const key = `fx:flare:${flash >= 1 ? 'full' : 'dim'}`;
    const sprite = new Sprite(
      this.cache.get(key, () => {
        const s = new PixelSurface(37, 21);
        isoDiamondRim(s, 18, 10, 9, hex(flash >= 1 ? '#ffffff' : '#cfe6e2'));
        isoDiamondRim(s, 18, 10, 6, SEMANTIC.warning, true, 0);
        return s.resetOps();
      }),
    );
    sprite.x = worldX(x, y) - 18;
    sprite.y = worldY(x, y) - 14;
    this.add(sprite, flash >= 1 ? 7 : 4, null);
  }

  puff(x: number, y: number): void {
    const sprite = new Sprite(
      this.cache.get('fx:puff', () => {
        const s = new PixelSurface(11, 7);
        isoDiamondRim(s, 5, 3, 3, shade(this.depth.enemyGlow, 0.7), true, 0);
        s.px(5, 3, this.depth.enemyGlow);
        return s.resetOps();
      }),
    );
    sprite.x = worldX(x, y) - 5;
    sprite.y = worldY(x, y) - 8;
    this.add(sprite, 8, null);
  }

  /** The Snapjaw hug: a hostile ring clamped around the grabbed tower. */
  grabFlash(wx: number, wy: number): void {
    const sprite = new Sprite(
      this.cache.get('fx:grab', () => {
        const s = new PixelSurface(30, 16);
        isoDiamondRim(s, 15, 8, 7, SEMANTIC.critical, true, 0);
        return s.resetOps();
      }),
    );
    sprite.x = wx - 15;
    sprite.y = wy - 8;
    this.add(sprite, 24, null);
  }

  /**
   * An upgrade landed: a ring of sparks lifts off the tower in the player's own
   * glow (doc 17 §7 — the purchase is acknowledged in the world, not only in
   * the panel). Deliberately NOT the hostile red of a Snapjaw grab.
   */
  upgradeBurst(wx: number, wy: number): void {
    const sprite = new Sprite(
      this.cache.get('fx:upgrade', () => {
        const s = new PixelSurface(28, 20);
        isoDiamondRim(s, 14, 12, 6, this.depth.playerGlow, true, 0);
        isoDiamondRim(s, 14, 10, 3, SEMANTIC.ink, true, 1);
        for (const [x, y] of [[4, 6], [10, 3], [17, 2], [23, 6], [14, 0]] as Array<[number, number]>) {
          s.px(x, y, this.depth.playerGlow);
        }
        return s.resetOps();
      }),
    );
    sprite.x = wx - 14;
    sprite.y = wy - 14;
    this.add(sprite, 14, null);
  }

  /** Hagfish acid, mid-flight: a short sour streak in the enemy's own glow. */
  spit(fromX: number, fromY: number, toX: number, toY: number): void {
    const x0 = worldX(fromX, fromY);
    const y0 = worldY(fromX, fromY) - 6;
    const x1 = worldX(toX, toY);
    const y1 = worldY(toX, toY) - 4;
    const minX = Math.min(x0, x1) - 2;
    const minY = Math.min(y0, y1) - 2;
    const s = new PixelSurface(Math.abs(x1 - x0) + 5, Math.abs(y1 - y0) + 5);
    linePx(s, x0 - minX, y0 - minY, x1 - minX, y1 - minY, shade(this.depth.enemyGlow, 1.1));
    const tex = surfaceToTexture(s);
    const sprite = new Sprite(tex);
    sprite.x = minX;
    sprite.y = minY;
    this.add(sprite, 5, tex);
  }

  /**
   * One enormous bite (doc 06 Leviathan). Deliberately louder than a chew: this
   * is 500 damage arriving at once and the player has to feel where it landed.
   */
  bite(x: number, y: number): void {
    const sprite = new Sprite(
      this.cache.get('fx:bite', () => {
        const s = new PixelSurface(40, 22);
        isoDiamondRim(s, 20, 11, 9, SEMANTIC.critical, true, 0);
        isoDiamondRim(s, 20, 11, 5, SEMANTIC.warning, true, 1);
        for (const [px, py] of [[6, 6], [14, 3], [26, 3], [34, 6], [20, 1]] as Array<[number, number]>) {
          s.px(px, py, SEMANTIC.critical);
        }
        return s.resetOps();
      }),
    );
    sprite.x = worldX(x, y) - 20;
    sprite.y = worldY(x, y) - 16;
    this.add(sprite, 10, null);
  }

  /**
   * Something left the water, or called something into it: a wide expanding
   * ring in the enemy glow. Used by the breach dive and the lure pulse.
   */
  ripple(x: number, y: number, radius: number): void {
    const halfH = Math.max(3, Math.round(radius * TH));
    const sprite = new Sprite(
      this.cache.get(`fx:ripple:${halfH}`, () => {
        const s = new PixelSurface(halfH * 4 + 6, halfH * 2 + 6);
        isoDiamondRim(s, s.width >> 1, s.height >> 1, halfH, this.depth.enemyGlow, true, 0);
        isoDiamondRim(s, s.width >> 1, s.height >> 1, Math.max(1, halfH - 3), shade(this.depth.enemyGlow, 0.5), true, 1);
        return s.resetOps();
      }),
    );
    sprite.x = worldX(x, y) - (sprite.texture.width >> 1);
    sprite.y = worldY(x, y) - (sprite.texture.height >> 1);
    this.add(sprite, 12, null);
  }

  /** The Q aim reticle lives here too so it can pulse with everything else. */
  reticleTexture(radiusTiles: number, parity: number): Texture {
    const halfH = radiusTiles * TH;
    return this.cache.get(`fx:reticle:${radiusTiles}:${parity}`, () => {
      const s = new PixelSurface(halfH * 4 + 4, halfH * 2 + 4);
      isoDiamondRim(s, s.width >> 1, s.height >> 1, halfH, OUTLINE_HOVER, true, parity);
      return s.resetOps();
    });
  }

  /** One step per SIM tick — effects live on sim time, pausing with it. */
  step(): void {
    for (let k = this.effects.length - 1; k >= 0; k--) {
      const e = this.effects[k]!;
      e.ttl--;
      if (e.lob) {
        const t = 1 - e.ttl / e.lob.total;
        const x = e.lob.x0 + (e.lob.x1 - e.lob.x0) * t;
        const arc = 26 * 4 * t * (1 - t); // peak 26 px at mid-flight
        const y = e.lob.y0 + (e.lob.y1 - e.lob.y0) * t - arc;
        e.sprite.x = Math.round(x);
        e.sprite.y = Math.round(y);
      }
      if (e.ttl <= 0) {
        e.sprite.destroy();
        e.own?.destroy(true);
        this.effects.splice(k, 1);
      }
    }
  }

  clear(): void {
    for (const e of this.effects) {
      e.sprite.destroy();
      e.own?.destroy(true);
    }
    this.effects.length = 0;
  }
}
